import { useCallback, useMemo, useState } from "react";
import { processError } from "../../../../common/utils/base_error";
import { useCreateDebounceHandler, useDebouncedCallback } from "../../../../common/hooks/use_debounced_callback";
import { useFuncionarioAssinaturasFilters } from "../hooks/CadastroAssinaturas_lista/filters";
import { useFuncionarioAssinaturasPagination } from "../hooks/CadastroAssinaturas_lista/pagination";
import { FuncionarioAssinaturaForm } from "../schemas/CadastroAssinaturas_lista/funcionario_assinatura";
import { FuncionarioAssinatura } from "../../domain/models/funcionario_assinatura";
import { usePageLoader } from "../../../../common/contexts/page_content_context";
import { useChangeHistory } from "../../../../common/hooks/use_change_history";
import { LogTipoTabela } from "../../../../common/utils/log_tabela_tipo";
import { usePageStartBootstrap } from "../../../../common/hooks/use_page_start_bootstrap";
import { useDependencyInjection } from "../../../../config/di/dependency_injection_context";
import { FuncionarioDropDown } from "../../../../common/domain/models/funcionario_dropdown";
import { useDropDownLoader } from "../../../../common/hooks/use_dropdown_loader";

/**
 * useCadastroFuncionarioAssinaturasListaViewModel
 */

type LoadCadastroFuncionarioAssinaturasState = {
  funcionario?: number;
  status?: number;
  page?: number;
  rowsPerPage?: number;
};

/**
 * useCadastroAssinaturasListaViewModel
 */

export const useCadastroAssinaturasListaViewModel = () => {
  const {
    commons: {
      getAllLogAlteracaoUseCase,
      getAllLogAlteracaoByParentUseCase,
    },
    rh: {
      getFuncionarioAssinaturasUseCase,
      createFuncionarioAssinaturaUseCase,
      updateFuncionarioAssinaturaUseCase,
      deleteFuncionarioAssinaturaUseCase,
      downloadFuncionarioAssinaturaUseCase,
    },
  } = useDependencyInjection();

  const { loadFuncionariosDropDown } = useDropDownLoader();

  const debouncedRef = useCreateDebounceHandler(750);

  const { pushErrorNotification, pushInfoNotification, setCustomLoading } = usePageLoader();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selectedFuncionarioAssinatura, setSelectedFuncionarioAssinatura] = useState<FuncionarioAssinatura>();

  const [funcionariosDropdown, setFuncionariosDropdown] = useState<FuncionarioDropDown[]>();

  const [openCadastroModal, setOpenCadastroModal] = useState(false);
  const [openDeleteModal, setOpenDeleteModal] = useState(false);

  const [errorMessage, setErrorMessage] = useState<string>();

  const [funcionarioAssinaturas, setFuncionarioAssinaturas] = useState<FuncionarioAssinatura[]>();

  const filters = useFuncionarioAssinaturasFilters();
  const pagination = useFuncionarioAssinaturasPagination();

  const changeHistory = useChangeHistory({
    getAllLogAlteracaoUseCase,
    getAllLogAlteracaoByParentUseCase,
    onSetError: (error) => pushErrorNotification("Erro", [error]),
  });

  const filteredFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      return x.canBeSelected;
    });
  }, [
    funcionariosDropdown,
  ]);

  const assinaturaFuncionariosDropdown = useMemo(() => {
    if (funcionariosDropdown === undefined) return null;
    return funcionariosDropdown.filter(x => {
      if (selectedFuncionarioAssinatura) {
        if (x.idFuncionario === selectedFuncionarioAssinatura.idFuncionario) {
          return true;
        }
      }
      return x.canBeSelected;
    });
  }, [
    selectedFuncionarioAssinatura,
    funcionariosDropdown,
  ]);

  /**
   * Funções de carregamento
   */

  const loadFuncionarioAssinaturas = useCallback(async (funcState?: LoadCadastroFuncionarioAssinaturasState) => {
    try {
      const filteredFuncionario = funcState?.funcionario ?? filters.funcionario;
      const filteredStatus = funcState?.status ?? filters.status;

      const filteredPage = funcState?.page ?? pagination.page;
      const filteredRowsPerPage = funcState?.rowsPerPage ?? pagination.rowsPerPage;

      await getFuncionarioAssinaturasUseCase.getFuncionarioAssinaturas(
        filteredFuncionario,
        filteredStatus,
        filteredPage,
        filteredRowsPerPage,
      );

      setFuncionarioAssinaturas(getFuncionarioAssinaturasUseCase.items);

      pagination.setRowsPerPage(getFuncionarioAssinaturasUseCase.pageSize);
      pagination.setTotalItems(getFuncionarioAssinaturasUseCase.totalItems);
    } catch (e: unknown) {
      setFuncionarioAssinaturas([]);
      throw e;
    }
  }, [
    filters,
    pagination,
    getFuncionarioAssinaturasUseCase,
  ]);

  /**
   * useEffect
   */

  usePageStartBootstrap(async () => {
    try {
      setErrorMessage(undefined);
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadFuncionarioAssinaturas();
    } catch (e: unknown) {
      setErrorMessage(processError(e, "Falha ao buscar assinaturas!")[0]);
    }
  });

  /**
   * Eventos
   */

  const handleReload = useCallback(async () => {
    try {
      setLoading(true);
      await loadFuncionariosDropDown(dt => setFuncionariosDropdown(dt), { includeCantBeSelected: true });
      await loadFuncionarioAssinaturas();
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionariosDropDown,
    loadFuncionarioAssinaturas,
    pushErrorNotification,
  ]);

  const handleSubmitFuncionarioAssinatura = useCallback(async (form: FuncionarioAssinaturaForm) => {
    if (!loading && !saving) {
      try {
        setSaving(true);

        if (!selectedFuncionarioAssinatura) {
          await createFuncionarioAssinaturaUseCase.createFuncionarioAssinatura(
            form.idFuncionario,
            form.nroRegistro,
            form.ativo,
            form.file,
          );
        } else {
          await updateFuncionarioAssinaturaUseCase.updateFuncionarioAssinatura(
            selectedFuncionarioAssinatura.idFuncionarioAssinatura,
            form.idFuncionario,
            form.nroRegistro,
            form.ativo,
            form.file,
            form.limparArquivos,
          );
        }

        await loadFuncionarioAssinaturas();
        pushInfoNotification('Sucesso', 'Registro salvo com sucesso!');
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, "Falha ao criar motivo de demissão!"));
      } finally {
        setSaving(false);
        setOpenCadastroModal(false);
        setSelectedFuncionarioAssinatura(undefined);
      }
    }
  }, [
    loading,
    saving,
    selectedFuncionarioAssinatura,
    updateFuncionarioAssinaturaUseCase,
    createFuncionarioAssinaturaUseCase,
    loadFuncionarioAssinaturas,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleFuncionarioChange = useDebouncedCallback(debouncedRef, (funcionario: number) => {
    useFuncionarioAssinaturasFilters.setState({ funcionario });
    return async () => {
      try {
        setLoading(true);
        pagination.setPage(1);
        await loadFuncionarioAssinaturas({ funcionario, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar assinaturas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFuncionarioAssinaturas,
    pushErrorNotification,
  ]);

  const handleStatusChange = useDebouncedCallback(debouncedRef, (status: number) => {
    useFuncionarioAssinaturasFilters.setState({ status });
    return async () => {
      try {
        setLoading(true);
        pagination.setPage(1);
        await loadFuncionarioAssinaturas({ status, page: 1 });
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar assinaturas!'));
      } finally {
        setLoading(false);
      }
    };
  }, [
    loadFuncionarioAssinaturas,
    pushErrorNotification,
  ]);

  const handleFuncionarioAssinaturaCadastroOpenState = useCallback((idFuncionarioAssinatura: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionarioAssinatura && funcionarioAssinaturas) {
        setSelectedFuncionarioAssinatura(funcionarioAssinaturas.find(x => x.idFuncionarioAssinatura === idFuncionarioAssinatura));
      } else {
        setSelectedFuncionarioAssinatura(undefined);
      }
    }
    setOpenCadastroModal(openState);
  }, [funcionarioAssinaturas]);

  const handleFuncionarioAssinaturaDeleteOpenState = useCallback((idFuncionarioAssinatura: number | null, openState: boolean) => {
    if (openState) {
      if (idFuncionarioAssinatura && funcionarioAssinaturas) {
        setSelectedFuncionarioAssinatura(funcionarioAssinaturas.find(x => x.idFuncionarioAssinatura === idFuncionarioAssinatura));
      } else {
        setSelectedFuncionarioAssinatura(undefined);
      }
    }
    setOpenDeleteModal(openState);
  }, [funcionarioAssinaturas]);

  const handlePageChange = useCallback(async (page: number) => {
    try {
      setLoading(true);
      useFuncionarioAssinaturasPagination.setState({ page: page + 1 });
      await loadFuncionarioAssinaturas({ page: page + 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarioAssinaturas,
    pushErrorNotification,
  ]);

  const handleRowsPerPageChange = useCallback(async (rowsPerPage: number) => {
    try {
      setLoading(true);
      useFuncionarioAssinaturasPagination.setState({ page: 1, rowsPerPage });
      await loadFuncionarioAssinaturas({ rowsPerPage, page: 1 });
    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
    } finally {
      setLoading(false);
    }
  }, [
    loadFuncionarioAssinaturas,
    pushErrorNotification,
  ]);

  const handleConfirmarDeletarFuncionarioAssinatura = useCallback(async () => {
    if (!saving && !loading) {
      try {
        setSaving(true);

        if (selectedFuncionarioAssinatura) {
          await deleteFuncionarioAssinaturaUseCase.deleteFuncionarioAssinatura(selectedFuncionarioAssinatura.idFuncionarioAssinatura);
          await loadFuncionarioAssinaturas();
          pushInfoNotification('Sucesso', 'Registro excluido com sucesso!');
        }
      } catch (e: unknown) {
        pushErrorNotification("Erro", processError(e, 'Falha ao buscar motivos de demissão!'));
      } finally {
        setSaving(false);
        setOpenDeleteModal(false);
        setSelectedFuncionarioAssinatura(undefined);
      }
    }
  }, [
    saving,
    loading,
    selectedFuncionarioAssinatura,
    deleteFuncionarioAssinaturaUseCase,
    loadFuncionarioAssinaturas,
    pushErrorNotification,
    pushInfoNotification,
  ]);

  const handleFuncionarioAssinaturaDownload = useCallback(async (idFuncionarioAssinatura: number, arquivoNome: string) => {
    try {
      setCustomLoading(true);

      await downloadFuncionarioAssinaturaUseCase
        .downloadFuncionarioAssinatura(idFuncionarioAssinatura, arquivoNome);

    } catch (e: unknown) {
      pushErrorNotification("Erro", processError(e, 'Falha ao fazer download do arquivo!'));
    } finally {
      setCustomLoading(false);
    }
  }, [
    downloadFuncionarioAssinaturaUseCase,
    pushErrorNotification,
    setCustomLoading,
  ]);

  const handleOpenChangeHistory = useCallback(() => {
    if (selectedFuncionarioAssinatura) {
      changeHistory.handleOpenChangeHistory(selectedFuncionarioAssinatura.idFuncionarioAssinatura,
        LogTipoTabela.FUNCIONARIO_ASSINATURA, false);
    }
  }, [
    selectedFuncionarioAssinatura,
    changeHistory,
  ]);

  return {
    loading,
    saving,
    selectedFuncionarioAssinatura,
    funcionariosDropdown: filteredFuncionariosDropdown ?? [],
    funcionariosAssinaturaDropdown: assinaturaFuncionariosDropdown ?? [],
    openCadastroModal,
    openDeleteModal,
    errorMessage,
    assinaturas: funcionarioAssinaturas ?? [],
    filters,
    pagination,
    changeHistory,
    handleReload,
    handleSubmitFuncionarioAssinatura,
    handleFuncionarioChange,
    handleStatusChange,
    handleFuncionarioAssinaturaCadastroOpenState,
    handleFuncionarioAssinaturaDeleteOpenState,
    handlePageChange,
    handleRowsPerPageChange,
    handleConfirmarDeletarFuncionarioAssinatura,
    handleFuncionarioAssinaturaDownload,
    handleOpenChangeHistory,
  };
}
